<template>
    <div class="validator-stake-changes" ref="componentTop">
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <div v-else>
            <div class="controls mb-4">
                <div class="row">
                    <div class="col-md-6 mb-3">
                        <input
                            v-model="searchQuery"
                            @input="onSearchChange"
                            type="text"
                            class="form-control"
                            placeholder="Search validator name or vote account..."
                        >
                    </div>
                    <div class="col-md-3 mb-3">
                        <select v-model="filterType" @change="fetchData" class="form-select">
                            <option value="all">All Validators</option>
                            <option value="gainers">Net Gainers</option>
                            <option value="losers">Net Losers</option>
                        </select>
                    </div>
                    <div class="col-md-3 mb-3 text-end">
                        <span class="text-muted">{{ total }} validators</span>
                    </div>
                </div>
            </div>

            <div class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th @click="sort('validator')" style="cursor: pointer;">
                                Validator
                                <i v-if="sortBy === 'validator'" :class="sortDesc ? 'fas fa-sort-down' : 'fas fa-sort-up'"></i>
                            </th>
                            <th @click="sort('deactivating')" style="cursor: pointer;" class="text-end">
                                Lost
                                <i v-if="sortBy === 'deactivating'" :class="sortDesc ? 'fas fa-sort-down' : 'fas fa-sort-up'"></i>
                            </th>
                            <th @click="sort('activating')" style="cursor: pointer;" class="text-end">
                                Gained
                                <i v-if="sortBy === 'activating'" :class="sortDesc ? 'fas fa-sort-down' : 'fas fa-sort-up'"></i>
                            </th>
                            <th @click="sort('net')" style="cursor: pointer;" class="text-end">
                                Net Change
                                <i v-if="sortBy === 'net'" :class="sortDesc ? 'fas fa-sort-down' : 'fas fa-sort-up'"></i>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="validator in validators" :key="validator.vote_account">
                            <td>
                                <a :href="`/validators/${validator.vote_account}`" class="text-decoration-none fw-semibold">
                                    {{ validator.name || validator.vote_account.substring(0, 8) + '...' }}
                                </a>
                            </td>
                            <td class="text-end">
                                <span v-if="validator.deactivating > 0" class="text-danger">
                                    {{ formatNumber(validator.deactivating) }}
                                </span>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td class="text-end">
                                <span v-if="validator.activating > 0" class="text-success">
                                    {{ formatNumber(validator.activating) }}
                                </span>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td class="text-end">
                                <span :class="getNetClass(validator.net)" class="fw-bold">
                                    {{ validator.net > 0 ? '+' : '' }}{{ formatNumber(validator.net) }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
                <nav>
                    <ul class="pagination">
                        <li class="page-item" :class="{ disabled: currentPage === 1 }">
                            <a class="page-link" @click.prevent="changePage(currentPage - 1)" href="#">Previous</a>
                        </li>
                        <li v-for="page in displayedPages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                            <a class="page-link" @click.prevent="changePage(page)" href="#">{{ page }}</a>
                        </li>
                        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                            <a class="page-link" @click.prevent="changePage(currentPage + 1)" href="#">Next</a>
                        </li>
                    </ul>
                </nav>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ValidatorStakeChanges',
    props: {
        epoch: {
            type: [String, Number],
            required: true
        }
    },
    data() {
        return {
            validators: [],
            searchQuery: '',
            filterType: 'all',
            sortBy: 'net',
            sortDesc: true,
            currentPage: 1,
            perPage: 50,
            total: 0,
            totalPages: 0,
            loading: true,
            searchTimeout: null
        };
    },
    computed: {
        displayedPages() {
            const pages = [];
            const maxDisplayed = 7;
            const half = Math.floor(maxDisplayed / 2);

            let start = Math.max(1, this.currentPage - half);
            let end = Math.min(this.totalPages, start + maxDisplayed - 1);

            if (end - start < maxDisplayed - 1) {
                start = Math.max(1, end - maxDisplayed + 1);
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            return pages;
        }
    },
    mounted() {
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.loading = true;
            try {
                const params = new URLSearchParams({
                    page: this.currentPage,
                    per_page: this.perPage,
                    sort_by: this.sortBy,
                    sort_desc: this.sortDesc,
                    filter_type: this.filterType,
                    search: this.searchQuery
                });

                const response = await fetch(`/api/validator-changes/${this.epoch}?${params}`);
                const data = await response.json();

                this.validators = data.data;
                this.total = data.total;
                this.totalPages = data.total_pages;
            } catch (error) {
                console.error('Error fetching validator changes:', error);
            } finally {
                this.loading = false;
            }
        },
        sort(field) {
            if (this.sortBy === field) {
                this.sortDesc = !this.sortDesc;
            } else {
                this.sortBy = field;
                this.sortDesc = true;
            }
            this.fetchData();
        },
        changePage(page) {
            if (page < 1 || page > this.totalPages) return;
            this.currentPage = page;
            this.fetchData();
            this.$nextTick(() => {
                this.$refs.componentTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        },
        onSearchChange() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1;
                this.fetchData();
            }, 500);
        },
        formatNumber(num) {
            return new Intl.NumberFormat('en-US', {
                maximumFractionDigits: 0
            }).format(num);
        },
        getNetClass(net) {
            if (net > 0) return 'text-success';
            if (net < 0) return 'text-danger';
            return 'text-secondary';
        }
    }
};
</script>

<style scoped>
.validator-stake-changes {
    font-size: 0.95rem;
}

.table th {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(236, 72, 153, 0.05));
    border-bottom: 2px solid rgba(139, 92, 246, 0.2);
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.05em;
}

.table-hover tbody tr:hover {
    background: linear-gradient(135deg, rgba(139, 92, 246, 0.03), rgba(236, 72, 153, 0.03));
}

.pagination .page-link {
    color: #8b5cf6;
    border-color: rgba(139, 92, 246, 0.2);
}

.pagination .page-item.active .page-link {
    background: linear-gradient(135deg, #8b5cf6, #ec4899);
    border-color: #8b5cf6;
}
</style>
